import { CommonModule } from "@angular/common"
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from "@angular/core"
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router"
import { AngularSvgIconModule } from "angular-svg-icon"
import packageJson from "../../../../../../package.json"
import { AclService } from "../../../../services/acl.service"
import { AuthService } from "../../../../services/auth.service"
import { DividerModule } from "primeng/divider"
import { Menu } from "primeng/menu"
import { MenuItem } from "primeng/api"
import { AccordionModule } from "primeng/accordion"
import { ButtonModule } from "primeng/button"
import { imagePlaceholder } from "../../../../utils/common"
import { ImgFallbackDirective } from "../../../common/img-fallback.directive"
import { filter, Subject, takeUntil } from "rxjs"
import { TranslateModule } from "@ngx-translate/core"

@Component({
	selector: "app-layout-sidebar-navbar",
	imports: [
		CommonModule,
		RouterLink,
		AccordionModule,
		ButtonModule,
		RouterLinkActive,
		AngularSvgIconModule,
		DividerModule,
		Menu,
		ImgFallbackDirective,
		TranslateModule
	],
	templateUrl: "./navbar.html",
	styleUrl: "./navbar.scss"
})
export class LayoutSidebarNavbar implements OnInit, OnDestroy {
	public readonly acl = inject(AclService)
	public readonly auth = inject(AuthService)
	private readonly router = inject(Router)
	private readonly destroy$ = new Subject<void>()
	public version = packageJson.version
	public imagePlaceholder = imagePlaceholder
	@ViewChild("menu") menu?: Menu
	@ViewChild("menuButton", { read: ElementRef }) menuButton?: ElementRef

	// Cache menu to avoid recreating the array on every change-detection cycle.
	private _mainMenu: MenuItem[] = []
	private _lastWorkgroupId: number | null = null
	private _workgroupMenu: MenuItem[] = []
	private _lastUserRole: string | null = null

	get workgroupMenu(): MenuItem[] {
		const currentUserRole = this.auth.userRole()

		// Rebuild only when user role changes (or on first access)
		if (!this._workgroupMenu.length || this._lastUserRole !== currentUserRole) {
			this._workgroupMenu = this.buildWorkgroupMenu()
			this._lastUserRole = currentUserRole
		}

		return this._workgroupMenu
	}

	get mainMenu(): MenuItem[] {
		const currentWorkgroupId = this.auth.workgroup()?.id ?? null

		// Rebuild only when workgroup changes (or on first access)
		if (!this._mainMenu.length || this._lastWorkgroupId !== currentWorkgroupId) {
			this._mainMenu = this.buildMainMenu()
			this._lastWorkgroupId = currentWorkgroupId
		}

		return this._mainMenu
	}

	public ngOnInit(): void {
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.closeWorkgroupMenu()
			})
	}

	@HostListener("document:click", ["$event"])
	public onDocumentClick(event: Event): void {
		if (
			this.menu?.visible &&
			this.menuButton &&
			!this.menuButton.nativeElement.contains(event.target) &&
			!this.menu.el.nativeElement.contains(event.target)
		) {
			this.closeWorkgroupMenu()
		}
	}

	public closeWorkgroupMenu() {
		if (this.menu) {
			this.menu.hide()
		}
	}

	public ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()
	}

	private buildWorkgroupMenu(): MenuItem[] {
		const list: MenuItem[] = []

		list.push({
			icon: "./assets/images/workgroups.svg",
			label: "NAVBAR.WORKGROUPS",
			routerLink: "/workgroups"
		})

		if (this.acl.can("user:view")) {
			list.push({
				icon: "./assets/images/user.svg",
				label: "NAVBAR.USERS",
				routerLink: "/users"
			})
		}

		return list
	}

	private buildMainMenu(): MenuItem[] {
		const list: MenuItem[] = []

		if (this.auth.workgroup()) {
			if (this.acl.can("tariff:view")) {
				list.push({
					routerLink: "/tariffs",
					icon: "./assets/images/tariffs.svg",
					label: "NAVBAR.TARIFFS"
				})
			}
			if (this.acl.can("emsp:view")) {
				list.push({
					icon: "./assets/images/emsp.svg",
					label: "NAVBAR.EMSP",
					items: [
						{
							routerLink: "/emsp",
							label: "NAVBAR.REGISTRY"
						},
						{
							routerLink: "/emsp-groups",
							label: "NAVBAR.GROUPS"
						}
					]
				})
			}
			if (this.acl.can("pdr:view")) {
				list.push({
					icon: "./assets/images/pdr.svg",
					label: "NAVBAR.PDR",
					items: [
						{
							routerLink: "/pdr",
							label: "NAVBAR.REGISTRY"
						},
						{
							routerLink: "/pdr-clusters",
							label: "NAVBAR.CLUSTER"
						}
					]
				})
			}
			if (this.acl.can("simulator:view")) {
				list.push({
					routerLink: "/simulator",
					icon: "./assets/images/simulator.svg",
					label: "NAVBAR.SIMULATOR"
				})
			}
		} else {
			list.push({
				routerLink: "/workgroups",
				icon: "./assets/images/workgroups.svg",
				label: "NAVBAR.WORKGROUPS"
			})

			if (this.acl.can("user:view")) {
				list.push({
					routerLink: "/users",
					icon: "./assets/images/user.svg",
					label: "NAVBAR.USERS"
				})
			}
		}

		return list
	}
}
